import { useMemo, useState } from 'react'

import { F0Box, SurveyFormBuilder } from '@factorialco/f0-react'
import { Tabs, Spinner } from '@factorialco/f0-react/dist/experimental'
import { FindWorkflowDocument, LoadVersionFormDocument } from 'generated/resources/graphql'

import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import {
  FromFragmentToQuestionnaire,
  FromQuestionnaireToForm,
} from 'modules/forms/components/newFormEditor/helpers'
import { useProcurementHandlers } from 'modules/procurement/components/procurementTypes/Workflows/useProcurementHandlers'
import { usePurchaseRequestDatasets } from 'modules/procurement/components/purchaseRequests/PurchaseRequestForm/datasets'
import { WorkflowContainer } from 'modules/workflows/components/WorkflowContainer'
import { WorkflowProvider } from 'modules/workflows/contexts/WorkflowProvider'

interface VersionPreviewProps {
  formId: string
  workflowId: string
  companyId: string
}

export function VersionPreview({ formId, workflowId, companyId }: VersionPreviewProps) {
  const [activeTab, setActiveTab] = useState('form')
  // Always enable all handlers in previews so existing nodes render correctly
  const handlers = useProcurementHandlers(true, true)

  const { datasets } = usePurchaseRequestDatasets(companyId)

  const { data: workflowData, loading: workflowLoading } = useQuery(FindWorkflowDocument, {
    variables: { companyId, workflowId },
    skip: !workflowId,
  })

  const { data: formQueryData, loading: loadingForm } = useQuery(LoadVersionFormDocument, {
    variables: { id: String(formId), resourceType: 'Procurement::Type' },
    skip: !formId,
  })

  const formData = formQueryData?.forms?.formsConnection?.edges?.[0]?.node

  const formElements = useMemo(() => {
    if (!formData) return []
    const questionnaire = FromFragmentToQuestionnaire(formData)
    return FromQuestionnaireToForm(questionnaire.elements, {
      mapDropdownSingleTo: 'dropdown-single',
    })
  }, [formData])

  const workflow = workflowData?.workflows?.workflowsConnection?.edges?.[0]?.node ?? null

  return (
    <F0Box display="flex" flexDirection="column" width="full" height="full">
      <Tabs
        activeTabId={activeTab}
        tabs={[
          {
            id: 'form',
            label: i18n.t('procurement.purchase_requests.settings.types.detail.tabs.form.title'),
            onClick: () => setActiveTab('form'),
          },
          {
            id: 'workflows',
            label: i18n.t(
              'procurement.purchase_requests.settings.types.detail.tabs.workflows.title'
            ),
            onClick: () => setActiveTab('workflows'),
          },
        ]}
      />

      <F0Box display="flex" grow overflow="hidden" minHeight="0">
        {activeTab === 'form' && (
          <>
            {loadingForm ? (
              <F0Box display="flex" alignItems="center" justifyContent="center" grow>
                <Spinner />
              </F0Box>
            ) : formElements.length > 0 ? (
              <F0Box width="full" height="full" overflow="auto" padding="md" paddingBottom="5xl">
                <SurveyFormBuilder
                  elements={formElements}
                  datasets={datasets}
                  onChange={() => {}}
                  disabled
                />
              </F0Box>
            ) : (
              <F0Box display="flex" alignItems="center" justifyContent="center" grow>
                {i18n.t('procurement.types.version_history.empty_state')}
              </F0Box>
            )}
          </>
        )}
        {activeTab === 'workflows' && (
          <>
            {workflowLoading ? (
              <F0Box display="flex" alignItems="center" justifyContent="center" grow>
                <Spinner />
              </F0Box>
            ) : workflow ? (
              <WorkflowProvider>
                <F0Box width="full" grow overflow="hidden" minHeight="0">
                  <WorkflowContainer
                    workflow={workflow}
                    handlers={handlers}
                    variables={{}}
                    readonly
                    trigger={{
                      title: i18n.t(
                        'procurement.purchase_requests.settings.types.detail.tabs.workflows.trigger.title'
                      ),
                      description: i18n.t(
                        'procurement.purchase_requests.settings.types.detail.tabs.workflows.trigger.description'
                      ),
                    }}
                  />
                </F0Box>
              </WorkflowProvider>
            ) : (
              <F0Box display="flex" alignItems="center" justifyContent="center" grow>
                {i18n.t('procurement.types.version_history.no_workflow')}
              </F0Box>
            )}
          </>
        )}
      </F0Box>
    </F0Box>
  )
}
